import { cloneElement, isValidElement, useId, type FocusEvent, type ReactNode } from 'react';

import { toneIcon } from '@mds/shared/lib/tone';
import { cn } from '@mds/shared/lib/utils';

const focusRing =
  'focus:border-brand-edge focus:outline-(length:--focus-ring-width) focus:outline-offset-(--focus-ring-offset) focus:outline-(color:--focus-ring-color)';

/** An invalid control: the shared controls' strong border over the danger wash. */
const invalidState = 'aria-invalid:border-destructive aria-invalid:bg-danger-wash';

/** Full-height field skin — for controls inside a modal or form body. */
export const inputCls = `w-full rounded-xl border border-ink/25 bg-surface px-4 py-3.5 text-lg text-ink transition-colors placeholder:text-ink-muted ${focusRing} ${invalidState}`;

/**
 * The full-height skin for a `<Select variant="field">`, laid over the shared
 * field box so a dropdown matches the `inputCls` text fields beside it. The
 * shared box is sized for the client's compact forms; the ring is dropped
 * because `inputCls` already brings the outline.
 */
export const selectCls = `${inputCls} h-auto md:text-lg shadow-none focus-visible:ring-0`;

/**
 * Compact field skin for card-header toolbars. Fixed at h-(--target-control) so inputs, selects
 * and buttons all line up on the same row — never rely on padding + line-height
 * to land three different control types on the same height.
 */
export const controlCls = `h-(--target-control) max-w-full rounded-xl border border-ink/25 bg-surface px-4 text-base text-ink transition-colors placeholder:text-ink-muted ${focusRing} ${invalidState}`;

/** A field error is a danger, so it wears that tone's mark rather than one of its own. */
const ErrorMark = toneIcon.danger;

/** What Field hands its control. Anything that takes these can be its child. */
type ControlProps = {
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  onBlur?: (event: FocusEvent<HTMLElement>) => void;
};

/**
 * A labelled form row. Wraps its child in a <label>, so the caller passes a bare
 * control.
 *
 * Like the shared Field, it hands the control `aria-describedby` and
 * `aria-invalid` rather than asking for them, so the message is always the one
 * a screen reader announces. An error replaces the hint instead of joining it,
 * and there is no separate invalid flag: an error is present or it is not.
 *
 * `onBlur` is passed down too, chained after the control's own, so a form can
 * spread `validation.field('name')` onto the Field and nothing else. React's
 * blur bubbles, so a control made of several elements reports it as well.
 */
export default function Field({
  label,
  hint,
  error,
  required,
  onBlur,
  children,
}: {
  label: string;
  hint?: string;
  /** Present means invalid. Replaces the hint and marks the control. */
  error?: string;
  /** Marks the label, so a form says up front which fields it needs. */
  required?: boolean;
  onBlur?: () => void;
  children: ReactNode;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const invalid = Boolean(error);
  const describedBy = invalid ? errorId : hint ? hintId : undefined;

  const control = isValidElement<ControlProps>(children)
    ? cloneElement(children, {
        'aria-describedby': children.props['aria-describedby'] ?? describedBy,
        'aria-invalid': children.props['aria-invalid'] ?? (invalid || undefined),
        onBlur: (event: FocusEvent<HTMLElement>) => {
          children.props.onBlur?.(event);
          onBlur?.();
        },
      })
    : children;

  return (
    <label className="flex flex-col gap-2" data-invalid={invalid || undefined}>
      <span
        className={cn(
          'font-mono text-sm font-semibold tracking-wider uppercase',
          invalid ? 'text-danger-text' : 'text-ink-secondary',
        )}
      >
        {label}
        {required && (
          <>
            <span className="text-danger-text" aria-hidden>
              {' *'}
            </span>
            <span className="sr-only"> (required)</span>
          </>
        )}
      </span>
      {control}
      {invalid ? (
        <FieldError id={errorId}>{error}</FieldError>
      ) : (
        hint && (
          <span id={hintId} className="text-base text-ink-muted">
            {hint}
          </span>
        )
      )}
    </label>
  );
}

/**
 * A field's error line, for a control that cannot sit inside a Field — a row of
 * a list, a picker that draws its own legend. Give the control
 * `aria-invalid` and point its `aria-describedby` at this `id`, so the message
 * is the one a screen reader announces with it.
 */
export function FieldError({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <span id={id} data-field-error className="flex items-start gap-1.5 text-base text-danger-text">
      <ErrorMark className="size-4 shrink-0 translate-y-1" aria-hidden />
      {children}
    </span>
  );
}

/**
 * The same line, drawn only when there is an error. Spread `useInlineField`'s
 * `message` onto it.
 */
export function InlineError({ id, error }: { id: string; error?: string }) {
  return error ? <FieldError id={id}>{error}</FieldError> : null;
}
